import { aptGenerationApi } from '@/api/apt_generation.api';
import { ActionButton } from '@/components/common/ActionButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { TableSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE, SECTION_LABEL } from '@/components/common/surfaces';
import { TextViewDialog } from '@/components/dialogs/TextViewDialog';
import { ToolpathDialog } from '@/components/dialogs/ToolpathDialog';
import { useDetections } from '@/hooks/useDetections';
import type { DrillSide } from '@/types/detection.types';
import { ORIENTATION_ORDER } from '@/types/detection.types';
import type { HoleObjectModel } from '@/types/apt_generation.types';
import type { ProcedureModel } from '@/types/procedure.types';
import type { PocketObjectModel, PocketProcedureModel } from '@/types/pocket.types';
import {
  TABLE_CARD,
  TABLE_CELL,
  TABLE_CELL_NUM,
  TABLE_HEADER_CELL,
  TABLE_HEADER_ROW,
  TABLE_ROW,
} from '@/utils/stickyColumns';
import { CaretDown, CaretRight, Cube, DownloadSimple, FileText, Path, WarningCircle } from '@phosphor-icons/react';
import { TabPanel, TabView } from 'primereact/tabview';
import { Fragment, type FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * The orientation tab strip, expressed as passthrough classes on PrimeReact's `TabView`.
 *
 * Every utility carries `!`. The bootstrap theme and the app's own PrimeReact overrides are
 * unlayered stylesheets, and in the cascade an unlayered `!important` declaration ranks *below*
 * a layered one — so the important variant is what lets a utility from Tailwind's `utilities`
 * layer win, whatever specificity the vendor selector happens to have.
 */
const TAB_STRIP_WRAPPER = [
  // The `ul`: one row of tabs over a single hairline. The theme draws its own bottom border and
  // `index.css` adds a second, darker one through `::after`; both are switched off here.
  '[&_.p-tabview-nav]:gap-1!',
  '[&_.p-tabview-nav]:border-0!',
  '[&_.p-tabview-nav]:border-b!',
  '[&_.p-tabview-nav]:border-gray-200!',
  '[&_.p-tabview-nav]:bg-transparent!',
  '[&_.p-tabview-nav]:after:hidden!',
  // A tab keeps its width and the strip scrolls, rather than six tabs squashing on a phone.
  '[&_.p-tabview-header]:shrink-0!',
  // The tab itself.
  '[&_.p-tabview-nav-link]:h-auto!',
  '[&_.p-tabview-nav-link]:rounded-t-lg!',
  '[&_.p-tabview-nav-link]:rounded-b-none!',
  '[&_.p-tabview-nav-link]:border-0!',
  '[&_.p-tabview-nav-link]:bg-transparent!',
  '[&_.p-tabview-nav-link]:px-3!',
  '[&_.p-tabview-nav-link]:py-2!',
  '[&_.p-tabview-nav-link]:text-sm!',
  '[&_.p-tabview-nav-link]:font-medium!',
  '[&_.p-tabview-nav-link]:text-gray-500!',
  '[&_.p-tabview-nav-link]:transition-all!',
  '[&_.p-tabview-nav-link]:duration-300!',
  '[&_.p-tabview-nav-link]:ease-[cubic-bezier(0.32,0.72,0,1)]!',
  '[&_.p-tabview-nav-link]:motion-reduce:transition-none!',
  '[&_.p-tabview-nav-link]:hover:text-gray-900!',
  '[&_.p-tabview-nav-link]:active:scale-[0.98]!',
  // The theme's focus ring is the bootstrap blue. This is the app accent, drawn inside the tab
  // because the link clips its own overflow.
  '[&_.p-tabview-nav-link]:focus-visible:shadow-[inset_0_0_0_2px_rgba(27,21,49,0.4)]!',
  // The panel under the strip brings only the gap; its content owns the rest.
  '[&_.p-tabview-panels]:bg-transparent!',
  '[&_.p-tabview-panels]:px-0!',
  '[&_.p-tabview-panels]:pb-0!',
  '[&_.p-tabview-panels]:pt-4!',
].join(' ');

/** The active tab: darker, heavier, and the only one carrying the accent bar. */
const TAB_HEADER_ACTIVE = '[&_.p-tabview-nav-link]:font-semibold! [&_.p-tabview-nav-link]:text-gray-900!';

interface PocketsPanelProps {
  projectId: string;
  modelId?: string | null;
}

function sortDetectionsByOrientation<T extends { orientation: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ia = ORIENTATION_ORDER.indexOf(a.orientation as DrillSide);
    const ib = ORIENTATION_ORDER.indexOf(b.orientation as DrillSide);
    if (ia === -1 && ib === -1) return a.orientation.localeCompare(b.orientation);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

interface TextDialogState {
  title: string;
  content: string;
  filename: string;
}

/** Header cell: the shared typography plus the padding and alignment the recipe leaves to the caller. */
const HEADER_CELL = `${TABLE_HEADER_CELL} px-3 text-left`;
const HEADER_CELL_NUM = `${TABLE_HEADER_CELL} px-3 text-right`;

/** The strip a pocket's procedures sit on, under the pocket's own summary row. */
const SUB_SURFACE = 'border-t border-gray-200 bg-[#fafbfb] px-4 py-3';

export const PocketsPanel: FC<PocketsPanelProps> = ({ projectId, modelId }) => {
  const { t } = useTranslation();
  const { detections, isLoading, error: detectionsError } = useDetections(projectId);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [pocketsData, setPocketsData] = useState<Record<string, PocketObjectModel[]>>({});
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [loadingExtractionId, setLoadingExtractionId] = useState<string | null>(null);
  const pocketsDataRef = useRef(pocketsData);
  pocketsDataRef.current = pocketsData;

  const [textDialog, setTextDialog] = useState<TextDialogState | null>(null);
  const [loadingFileKey, setLoadingFileKey] = useState<string | null>(null);
  const [expandedHFiles, setExpandedHFiles] = useState<Set<string>>(new Set());
  const [isDownloadingPocketsZip, setIsDownloadingPocketsZip] = useState(false);
  const [downloadingZipProcId, setDownloadingZipProcId] = useState<string | null>(null);
  const [toolpathDialog, setToolpathDialog] = useState<{ procedureId: string; title: string } | null>(null);

  const [chamfersData, setChamfersData] = useState<Record<string, HoleObjectModel[]>>({});

  const pocketExtractions = useMemo(() => {
    if (!modelId) return [];
    return sortDetectionsByOrientation(
      detections.filter((d) => d.extraction_type === 'pockets' && d.model_id === modelId)
    );
  }, [detections, modelId]);

  const holeExtractions = useMemo(() => {
    if (!modelId) return [];
    return sortDetectionsByOrientation(
      detections.filter((d) => d.extraction_type === 'holes' && d.model_id === modelId)
    );
  }, [detections, modelId]);

  useEffect(() => {
    if (activeTabIndex >= pocketExtractions.length && pocketExtractions.length > 0) {
      setActiveTabIndex(0);
    }
  }, [activeTabIndex, pocketExtractions.length]);

  useEffect(() => {
    const ext = pocketExtractions[activeTabIndex];
    if (!ext) return;
    if (pocketsDataRef.current[ext.id] !== undefined) return;

    let cancelled = false;
    setFetchError(null);
    setLoadingExtractionId(ext.id);

    void aptGenerationApi
      .getPocketsByExtractionId(ext.id)
      .then((rows) => {
        if (!cancelled) {
          setPocketsData((prev) => ({ ...prev, [ext.id]: rows }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchError(t('projectDetail.pocketsFetchError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingExtractionId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTabIndex, pocketExtractions, t]);

  // Fetch chamfers for the holes extraction matching the active tab orientation
  useEffect(() => {
    const pocketExt = pocketExtractions[activeTabIndex];
    if (!pocketExt) return;
    const holeExt = holeExtractions.find((h) => h.orientation === pocketExt.orientation);
    if (!holeExt) return;
    if (chamfersData[holeExt.id] !== undefined) return;

    let cancelled = false;
    void aptGenerationApi
      .getChamfersByExtractionId(holeExt.id)
      .then((rows) => {
        if (!cancelled) setChamfersData((prev) => ({ ...prev, [holeExt.id]: rows }));
      })
      .catch(() => {
        /* chamfers are optional — silently ignore */
      });

    return () => {
      cancelled = true;
    };
  }, [activeTabIndex, pocketExtractions, holeExtractions, chamfersData]);

  const handleViewAptSource = async (proc: PocketProcedureModel) => {
    const key = `apt-${proc.id}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getPocketProcedureHProgram(proc.id);
      const text = await blob.text();
      const filename = `pocket_procedure_${proc.id}.h`;
      setTextDialog({
        title: `${t('projectDetail.pocketsAptSourceDialogTitle')} — pass ${proc.pass_index}`,
        content: text,
        filename,
      });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleViewHFile = async (proc: PocketProcedureModel, filename: string) => {
    const key = `h-${proc.id}-${filename}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getPocketProcedureHFile(proc.id, filename);
      const text = await blob.text();
      setTextDialog({
        title: `${t('projectDetail.pocketsHFileDialogTitle')} — ${filename}`,
        content: text,
        filename,
      });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const toggleHFiles = (procId: string) => {
    setExpandedHFiles((prev) => {
      const next = new Set(prev);
      if (next.has(procId)) {
        next.delete(procId);
      } else {
        next.add(procId);
      }
      return next;
    });
  };

  const handleDownloadProcedureHFilesZip = async (procId: string) => {
    setDownloadingZipProcId(procId);
    try {
      const response = await aptGenerationApi.downloadPocketProcedureHFilesZip(procId);
      let filename = `h-files_${procId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setDownloadingZipProcId(null);
    }
  };

  const handleDownloadPocketsZip = async () => {
    setIsDownloadingPocketsZip(true);
    try {
      const response = await aptGenerationApi.downloadProjectPocketsHFilesZip(projectId);
      let filename = `h_files_pockets_project_${projectId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setIsDownloadingPocketsZip(false);
    }
  };

  const handleViewChamferAptSource = async (proc: ProcedureModel) => {
    const key = `apt-${proc.id}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getProcedureHProgram(proc.id);
      const text = await blob.text();
      setTextDialog({
        title: `${t('projectDetail.pocketsAptSourceDialogTitle')} — ${proc.operation_name}`,
        content: text,
        filename: `chamfer_${proc.id}.h`,
      });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleViewChamferHFile = async (proc: ProcedureModel, filename: string) => {
    const key = `h-${proc.id}-${filename}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getProcedureHFile(proc.id, filename);
      const text = await blob.text();
      setTextDialog({ title: `${t('projectDetail.pocketsHFileDialogTitle')} — ${filename}`, content: text, filename });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleDownloadChamferHFilesZip = async (procId: string) => {
    setDownloadingZipProcId(procId);
    try {
      const response = await aptGenerationApi.downloadProcedureHFilesZip(procId);
      let filename = `h-files_${procId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setDownloadingZipProcId(null);
    }
  };

  /** The panel heading, identical in every state the panel can be in. */
  const panelHeader = (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className={ICON_TILE}>
          <Cube size={18} aria-hidden="true" />
        </span>
        <h2 className={`m-0 ${PANEL_TITLE}`}>{t('projectDetail.pocketsTitle')}</h2>
      </div>
      <ActionButton
        variant="secondary"
        icon={<DownloadSimple size={16} aria-hidden="true" />}
        onClick={() => handleDownloadPocketsZip()}
        loading={isDownloadingPocketsZip}
        title={t('projectDetail.downloadProjectPocketsHFilesTooltip')}
        aria-label={t('projectDetail.downloadProjectPocketsHFilesTooltip')}
        className="shrink-0"
      >
        {t('projectDetail.downloadProjectPocketsHFiles')}
      </ActionButton>
    </div>
  );

  /** The heading without the download action, for the states where there is nothing to download. */
  const plainHeader = (
    <div className="flex min-w-0 items-center gap-3">
      <span className={ICON_TILE}>
        <Cube size={18} aria-hidden="true" />
      </span>
      <h2 className={`m-0 ${PANEL_TITLE}`}>{t('projectDetail.pocketsTitle')}</h2>
    </div>
  );

  if (!modelId) {
    return (
      <div className={CARD_PADDED}>
        {plainHeader}
        <EmptyState
          title={t('projectDetail.pocketsTitle')}
          message={t('projectDetail.pocketsNoTargetModel')}
          icon={<Cube size={28} aria-hidden="true" />}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`${CARD_PADDED} flex flex-col gap-4`}>
        {plainHeader}
        <TableSkeleton rows={3} columns={5} />
      </div>
    );
  }

  if (detectionsError) {
    return (
      <div className={`${CARD_PADDED} flex flex-col gap-4`}>
        {plainHeader}
        <ErrorMessage message={detectionsError} />
      </div>
    );
  }

  if (pocketExtractions.length === 0) {
    return (
      <div className={CARD_PADDED}>
        {plainHeader}
        <EmptyState
          title={t('projectDetail.pocketsTitle')}
          message={t('projectDetail.pocketsNoExtractions')}
          icon={<Cube size={28} aria-hidden="true" />}
        />
      </div>
    );
  }

  return (
    <>
      <div className={`${CARD_PADDED} flex flex-col gap-4`}>
        {panelHeader}

        {fetchError ? <ErrorMessage message={fetchError} /> : null}

        <div className={TAB_STRIP_WRAPPER}>
          <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
            {pocketExtractions.map((extraction, tabIndex) => {
              const isActiveTab = tabIndex === activeTabIndex;
              const pockets = pocketsData[extraction.id];
              const count = pockets !== undefined ? pockets.length : '…';
              const sortedPockets =
                pockets !== undefined ? [...pockets].sort((a, b) => a.order_number - b.order_number) : [];

              const holeExt = holeExtractions.find((h) => h.orientation === extraction.orientation);
              const chamferHoles = holeExt ? (chamfersData[holeExt.id] ?? null) : null;
              // Group chamfer holes by their shared procedure (same procedure shared across all holes in a group)
              const chamferGroups: Map<string, { proc: ProcedureModel; holes: HoleObjectModel[] }> = new Map();
              if (chamferHoles) {
                for (const hole of chamferHoles) {
                  for (const proc of hole.procedures) {
                    if (!chamferGroups.has(proc.id)) {
                      chamferGroups.set(proc.id, { proc, holes: [] });
                    }
                    chamferGroups.get(proc.id)!.holes.push(hole);
                  }
                }
              }
              const chamferGroupList = [...chamferGroups.values()].sort(
                (a, b) => a.proc.order_number - b.proc.order_number
              );

              return (
                <TabPanel
                  key={extraction.id}
                  pt={{ header: { className: isActiveTab ? TAB_HEADER_ACTIVE : undefined } }}
                  header={
                    <span className="flex items-center gap-2">
                      <span>{extraction.orientation}</span>
                      <span className="text-xs tabular-nums text-gray-400">({count})</span>
                      {isActiveTab && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" aria-hidden="true" />
                      )}
                    </span>
                  }
                >
                  {loadingExtractionId === extraction.id && pockets === undefined ? (
                    <TableSkeleton rows={3} columns={5} />
                  ) : pockets !== undefined && sortedPockets.length === 0 ? (
                    <EmptyState
                      title={t('projectDetail.pocketsTitle')}
                      message={t('projectDetail.pocketsEmptyOrientation')}
                      icon={<Cube size={28} aria-hidden="true" />}
                    />
                  ) : pockets !== undefined ? (
                    <div className="flex flex-col gap-4">
                      {sortedPockets.map((pocket) => (
                        <div key={pocket.id} className={`${TABLE_CARD} [contain:paint]`}>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className={TABLE_HEADER_ROW}>
                                  <th className={HEADER_CELL}>{t('projectDetail.pocketsFeatureId')}</th>
                                  <th className={HEADER_CELL}>{t('projectDetail.pocketsKind')}</th>
                                  <th className={HEADER_CELL_NUM}>{t('projectDetail.pocketsOrder')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className={TABLE_ROW}>
                                  <td className={`${TABLE_CELL} font-mono text-xs`}>{pocket.feature_id}</td>
                                  <td className={TABLE_CELL}>{pocket.kind}</td>
                                  <td className={TABLE_CELL_NUM}>{pocket.order_number}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className={SUB_SURFACE}>
                            <p className={`m-0 mb-2 ${SECTION_LABEL}`}>{t('projectDetail.pocketsProceduresHeading')}</p>
                            {pocket.pocket_procedures.length === 0 ? (
                              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-700">
                                <WarningCircle size={16} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
                                <div className="min-w-0">
                                  <span>{t('projectDetail.pocketsAptFailed')}</span>
                                  {pocket.apt_error && (
                                    <p className="m-0 mt-1 break-all text-xs text-red-700">{pocket.apt_error}</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className={HEADER_CELL}>{t('projectDetail.pocketsPass')}</th>
                                      <th className={HEADER_CELL}>{t('projectDetail.pocketsOperation')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('projectDetail.toolNumber')}</th>
                                      <th className={HEADER_CELL}>{t('toolManagement.toolName')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('projectDetail.spindleSpeed')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('toolManagement.feedMachValue')}</th>
                                      <th className="px-3" aria-hidden="true" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...pocket.pocket_procedures]
                                      .sort((a, b) => a.pass_index - b.pass_index)
                                      .map((proc) => {
                                        const hasApt = Boolean(proc.apt_s3_url);
                                        const hFilesList = proc.h_files ?? [];
                                        const hasPplog = Boolean(proc.h_files_pplog);
                                        const hasHFiles = hFilesList.length > 0 || hasPplog;
                                        const hFilesExpanded = expandedHFiles.has(proc.id);

                                        return (
                                          <Fragment key={proc.id}>
                                            <tr className={`${TABLE_ROW} ${proc.error ? 'bg-red-50' : ''}`}>
                                              <td className={TABLE_CELL}>
                                                <div className="flex min-w-0 items-center gap-1">
                                                  {hasHFiles ? (
                                                    <ActionButton
                                                      variant="ghost"
                                                      iconOnly={true}
                                                      icon={
                                                        hFilesExpanded ? (
                                                          <CaretDown size={14} aria-hidden="true" />
                                                        ) : (
                                                          <CaretRight size={14} aria-hidden="true" />
                                                        )
                                                      }
                                                      onClick={() => toggleHFiles(proc.id)}
                                                      aria-expanded={hFilesExpanded}
                                                      title={t('projectDetail.pocketsViewHFiles')}
                                                      aria-label={t('projectDetail.pocketsViewHFiles')}
                                                      className="h-6 w-6 shrink-0"
                                                    />
                                                  ) : (
                                                    <span className="w-6 shrink-0" aria-hidden="true" />
                                                  )}
                                                  <span className="tabular-nums">{proc.pass_index}</span>
                                                </div>
                                              </td>
                                              <td className={TABLE_CELL}>{proc.operation_name}</td>
                                              <td className={TABLE_CELL_NUM}>
                                                {proc.tool_number != null ? proc.tool_number : '—'}
                                              </td>
                                              <td className={TABLE_CELL}>{proc.tool_name ?? '—'}</td>
                                              <td className={TABLE_CELL_NUM}>{proc.spindle_speed_rpm}</td>
                                              <td className={TABLE_CELL_NUM}>{proc.feed_cut}</td>
                                              <td className={`${TABLE_CELL} text-right`}>
                                                <div className="flex items-center justify-end gap-1">
                                                  {hasApt && (
                                                    <ActionButton
                                                      variant="ghost"
                                                      icon={<FileText size={14} aria-hidden="true" />}
                                                      disabled={loadingFileKey === `apt-${proc.id}`}
                                                      onClick={() => handleViewAptSource(proc)}
                                                      title={t('projectDetail.pocketsViewAptSource')}
                                                      className="px-2 py-1 text-xs"
                                                    >
                                                      APT
                                                    </ActionButton>
                                                  )}
                                                  {proc.toolpath_html_s3_url && (
                                                    <ActionButton
                                                      variant="ghost"
                                                      icon={<Path size={14} aria-hidden="true" />}
                                                      onClick={() =>
                                                        setToolpathDialog({
                                                          procedureId: proc.id,
                                                          title: `${t('projectDetail.toolpathDialogTitle')} — ${pocket.feature_id} · pass ${proc.pass_index}`,
                                                        })
                                                      }
                                                      title={t('projectDetail.toolpathButtonTooltip')}
                                                      className="px-2 py-1 text-xs"
                                                    >
                                                      {t('projectDetail.toolpathButtonLabel')}
                                                    </ActionButton>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                            {proc.error && (
                                              <tr className="border-b border-gray-100 bg-red-50">
                                                <td colSpan={7} className="px-3 py-2 pl-9">
                                                  <div className="flex items-start gap-2 text-xs text-red-700">
                                                    <WarningCircle
                                                      size={14}
                                                      className="mt-0.5 shrink-0 text-red-600"
                                                      aria-hidden="true"
                                                    />
                                                    <span className="break-all">{proc.error}</span>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                            {hFilesExpanded && (
                                              <>
                                                <tr className="border-b border-gray-200 bg-gray-50">
                                                  <td colSpan={7} className="px-3 py-2">
                                                    <div className="flex w-full min-w-0 flex-wrap items-center gap-2 pl-6">
                                                      <span className={SECTION_LABEL}>
                                                        {t('projectDetail.hFilesSectionLabel')}
                                                      </span>
                                                      <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                                                        {hasPplog && proc.h_files_pplog && (
                                                          <ActionButton
                                                            variant="ghost"
                                                            icon={<FileText size={14} aria-hidden="true" />}
                                                            disabled={
                                                              loadingFileKey === `h-${proc.id}-${proc.h_files_pplog}`
                                                            }
                                                            onClick={() => handleViewHFile(proc, proc.h_files_pplog!)}
                                                            title={t('projectDetail.viewPplogFile')}
                                                            aria-label={t('projectDetail.viewPplogFile')}
                                                            className="min-w-0 max-w-full px-2 py-1 text-xs"
                                                          >
                                                            <span className="min-w-0 truncate">
                                                              {proc.h_files_pplog}
                                                            </span>
                                                          </ActionButton>
                                                        )}
                                                        <ActionButton
                                                          variant="ghost"
                                                          icon={<DownloadSimple size={14} aria-hidden="true" />}
                                                          disabled={downloadingZipProcId === proc.id}
                                                          onClick={() => handleDownloadProcedureHFilesZip(proc.id)}
                                                          title={t('projectDetail.downloadHFilesZipTooltip')}
                                                          aria-label={t('projectDetail.downloadHFilesZipTooltip')}
                                                          className="px-2 py-1 text-xs"
                                                        >
                                                          {t('projectDetail.downloadHFilesZipButton')}
                                                        </ActionButton>
                                                      </div>
                                                    </div>
                                                  </td>
                                                </tr>
                                                {hFilesList.map((filename) => (
                                                  <tr key={filename} className={TABLE_ROW}>
                                                    <td colSpan={7} className={TABLE_CELL}>
                                                      <div className="flex min-w-0 items-center gap-2 pl-6">
                                                        <span className="min-w-0 break-all text-xs">{filename}</span>
                                                        <ActionButton
                                                          variant="ghost"
                                                          iconOnly={true}
                                                          icon={<FileText size={14} aria-hidden="true" />}
                                                          disabled={loadingFileKey === `h-${proc.id}-${filename}`}
                                                          onClick={() => handleViewHFile(proc, filename)}
                                                          title={t('projectDetail.viewHFile')}
                                                          aria-label={t('projectDetail.viewHFile')}
                                                          className="shrink-0"
                                                        />
                                                      </div>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </>
                                            )}
                                          </Fragment>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <TableSkeleton rows={3} columns={5} />
                  )}

                  {/* ── Chamfers section ── */}
                  {chamferGroupList.length > 0 && (
                    <div className="mt-6 flex flex-col gap-3">
                      <p className={`m-0 ${SECTION_LABEL}`}>{t('projectDetail.chamfersHeading')}</p>
                      <div className="flex flex-col gap-4">
                        {chamferGroupList.map(({ proc, holes }) => {
                          const hasApt = Boolean(proc.apt_s3_url);
                          const hFilesList = proc.h_files ?? [];
                          const hasPplog = Boolean(proc.h_files_pplog);
                          const hasHFiles = hFilesList.length > 0 || hasPplog;
                          const hFilesExpanded = expandedHFiles.has(proc.id);
                          return (
                            <div key={proc.id} className={`${TABLE_CARD} [contain:paint]`}>
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                  <thead>
                                    <tr className={TABLE_HEADER_ROW}>
                                      <th className={HEADER_CELL}>{t('projectDetail.pocketsOperation')}</th>
                                      <th className={HEADER_CELL}>{t('toolManagement.toolName')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('projectDetail.spindleSpeed')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('toolManagement.feedMachValue')}</th>
                                      <th className={HEADER_CELL_NUM}>{t('projectDetail.chamfersHolesCount')}</th>
                                      <th className="px-3" aria-hidden="true" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className={TABLE_ROW}>
                                      <td className={`${TABLE_CELL} font-mono text-xs`}>{proc.operation_name}</td>
                                      <td className={TABLE_CELL}>{proc.assembly.tool?.tool_name ?? '—'}</td>
                                      <td className={TABLE_CELL_NUM}>{proc.spindle_speed_rpm}</td>
                                      <td className={TABLE_CELL_NUM}>{proc.feed_cut}</td>
                                      <td className={TABLE_CELL_NUM}>{holes.length}</td>
                                      <td className={`${TABLE_CELL} text-right`}>
                                        <div className="flex items-center justify-end gap-1">
                                          {hasHFiles && (
                                            <ActionButton
                                              variant="ghost"
                                              iconOnly={true}
                                              icon={
                                                hFilesExpanded ? (
                                                  <CaretDown size={14} aria-hidden="true" />
                                                ) : (
                                                  <CaretRight size={14} aria-hidden="true" />
                                                )
                                              }
                                              onClick={() => toggleHFiles(proc.id)}
                                              aria-expanded={hFilesExpanded}
                                              title={t('projectDetail.pocketsViewHFiles')}
                                              aria-label={t('projectDetail.pocketsViewHFiles')}
                                            />
                                          )}
                                          {hasApt && (
                                            <ActionButton
                                              variant="ghost"
                                              icon={<FileText size={14} aria-hidden="true" />}
                                              disabled={loadingFileKey === `apt-${proc.id}`}
                                              onClick={() => handleViewChamferAptSource(proc)}
                                              title={t('projectDetail.pocketsViewAptSource')}
                                              className="px-2 py-1 text-xs"
                                            >
                                              APT
                                            </ActionButton>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                    {hFilesExpanded && (
                                      <>
                                        <tr className="border-b border-gray-200 bg-gray-50">
                                          <td colSpan={6} className="px-3 py-2">
                                            <div className="flex w-full flex-wrap items-center gap-2 pl-4">
                                              <span className={SECTION_LABEL}>
                                                {t('projectDetail.hFilesSectionLabel')}
                                              </span>
                                              <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                                                {hasPplog && proc.h_files_pplog && (
                                                  <ActionButton
                                                    variant="ghost"
                                                    icon={<FileText size={14} aria-hidden="true" />}
                                                    disabled={loadingFileKey === `h-${proc.id}-${proc.h_files_pplog}`}
                                                    onClick={() => handleViewChamferHFile(proc, proc.h_files_pplog!)}
                                                    title={t('projectDetail.viewPplogFile')}
                                                    aria-label={t('projectDetail.viewPplogFile')}
                                                    className="min-w-0 max-w-full px-2 py-1 text-xs"
                                                  >
                                                    <span className="min-w-0 truncate">{proc.h_files_pplog}</span>
                                                  </ActionButton>
                                                )}
                                                <ActionButton
                                                  variant="ghost"
                                                  icon={<DownloadSimple size={14} aria-hidden="true" />}
                                                  disabled={downloadingZipProcId === proc.id}
                                                  onClick={() => handleDownloadChamferHFilesZip(proc.id)}
                                                  title={t('projectDetail.downloadHFilesZipTooltip')}
                                                  aria-label={t('projectDetail.downloadHFilesZipTooltip')}
                                                  className="px-2 py-1 text-xs"
                                                >
                                                  {t('projectDetail.downloadHFilesZipButton')}
                                                </ActionButton>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                        {hFilesList.map((filename: string) => (
                                          <tr key={filename} className={TABLE_ROW}>
                                            <td colSpan={6} className={TABLE_CELL}>
                                              <div className="flex min-w-0 items-center gap-2 pl-4">
                                                <span className="min-w-0 break-all text-xs">{filename}</span>
                                                <ActionButton
                                                  variant="ghost"
                                                  iconOnly={true}
                                                  icon={<FileText size={14} aria-hidden="true" />}
                                                  disabled={loadingFileKey === `h-${proc.id}-${filename}`}
                                                  onClick={() => handleViewChamferHFile(proc, filename)}
                                                  title={t('projectDetail.viewHFile')}
                                                  aria-label={t('projectDetail.viewHFile')}
                                                  className="shrink-0"
                                                />
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </TabView>
        </div>
      </div>

      {textDialog && (
        <TextViewDialog
          visible={true}
          title={textDialog.title}
          textContent={textDialog.content}
          downloadFilename={textDialog.filename}
          onClose={() => setTextDialog(null)}
        />
      )}

      <ToolpathDialog
        visible={toolpathDialog !== null}
        title={toolpathDialog?.title ?? ''}
        pocketProcedureId={toolpathDialog?.procedureId ?? null}
        onClose={() => setToolpathDialog(null)}
      />
    </>
  );
};
